import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  Typography,
  List,
  Descriptions,
  Space,
  Button,
  Divider,
  ConfigProvider,
  Result,
  Spin,
} from "antd";
import {
  CheckCircleOutlined,
  CopyOutlined,
  DownloadOutlined,
  QuestionCircleOutlined,
  UserOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import styles from "./index.module.css";
import moment from "moment";
import { baseUrl } from "../../utils/api";
import checkFilled from "../../utils/img/checkFilled.png";
import viewPolicy from "../../utils/pdf/viewCover.docx.pdf";

const { Title, Text, Paragraph } = Typography;

const Index = () => {
  const navigate = useNavigate();
  const [confirmationDetails, setConfirmationDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const coverageCountry = localStorage.getItem("coverageCountry");

  const apiCalledRef = useRef(false);
  const token = localStorage.getItem("token");

  // Function to get userId from all possible sources
  const getUserId = useCallback(() => {
    // Try localStorage first
    const localUserId = localStorage.getItem("userId");
    if (localUserId) return localUserId;

    // Try to get from user details in sessionStorage
    const userDetailsStr = sessionStorage.getItem("user");
    if (userDetailsStr) {
      try {
        const userDetails = JSON.parse(userDetailsStr);
        if (userDetails?.userId) {
          localStorage.setItem("userId", userDetails.userId);
          return userDetails.userId;
        }
      } catch (e) {
        console.error("Error parsing user details:", e);
      }
    }

    return null;
  }, []);

  const pullData = useCallback(async () => {
    const userid = getUserId();

    if (!userid) {
      setError("User ID not found. Please ensure you're logged in.");
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const { data } = await axios({
        url: `${baseUrl}/quote/policy/${userid}`,
        method: "POST",
        headers: {
          "content-type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (data?.status === 200) {
        sessionStorage.setItem("accountId", data?.result?.account_id);
        sessionStorage.setItem("policyDetails", JSON.stringify(data?.result));
        setConfirmationDetails(data?.result);
      } else {
        setError(data?.message || "Could not fetch policy details.");
      }
    } catch (err) {
      let apiError = "Could not fetch policy details.";
      if (err?.response?.data?.result?.message) {
        apiError = err.response.data.result.message;
      } else if (err?.message) {
        apiError = err.message;
      }
      setError(apiError);
    } finally {
      setIsLoading(false);
    }
  }, [getUserId]);

  useEffect(() => {
    const fetchData = async () => {
      // Check cache first
      const cachedData = sessionStorage.getItem("policyDetails");
      if (cachedData) {
        setConfirmationDetails(JSON.parse(cachedData));
        setIsLoading(false);
        return;
      }

      // If API hasn't been called yet, fetch data
      if (!apiCalledRef.current) {
        apiCalledRef.current = true;
        await pullData();
      }
    };

    fetchData();
  }, [pullData]);

  // NOW check for user session AFTER all hooks
  if (!sessionStorage.getItem("user")) {
    return (
      <div className={styles.main}>
        <Card style={{ maxWidth: 800, margin: "auto", marginTop: 48 }}>
          <Result
            status="warning"
            title="Session Expired"
            subTitle="Your session has expired or is invalid. Please start your application again."
            extra={[
              <Button
                type="primary"
                onClick={() => (window.location.href = "/")}
                key="start"
              >
                Start Again
              </Button>,
            ]}
          />
        </Card>
      </div>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className={styles.main}>
        <Card style={{ maxWidth: 800, margin: "auto", marginTop: 48 }}>
          <div style={{ textAlign: "center", padding: "48px 0" }}>
            <Spin size="large" />
            <p style={{ marginTop: 16 }}>Loading your policy details...</p>
          </div>
        </Card>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className={styles.main}>
        <Card style={{ maxWidth: 800, margin: "auto", marginTop: 48 }}>
          <Result
            status="error"
            title="Unable to Load Policy Details"
            subTitle={error}
            extra={[
              <Button
                type="primary"
                onClick={() => {
                  apiCalledRef.current = false;
                  setIsLoading(true);
                  setError(null);
                  pullData();
                }}
                key="retry"
              >
                Retry
              </Button>,
              <Button onClick={() => (window.location.href = "/")} key="home">
                Return to Home
              </Button>,
            ]}
          />
        </Card>
      </div>
    );
  }

  // Main content render
  return (
    <div className={styles.main}>
      <Card style={{ maxWidth: 800, margin: "auto" }}>
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "48px 0" }}>
            <Spin size="large" />
          </div>
        ) : error ? (
          <Result
            status="error"
            title="Failed to Load Policy Details"
            subTitle={error}
            extra={[
              <Button
                type="primary"
                onClick={() => {
                  const cachedData = sessionStorage.getItem("policyDetails");
                  if (cachedData) {
                    setConfirmationDetails(JSON.parse(cachedData));
                    setError(null);
                    setIsLoading(false);
                  } else {
                    apiCalledRef.current = false; // Reset the ref to allow a new API call
                    pullData();
                  }
                }}
                key="reload"
              >
                Retry
              </Button>,
            ]}
          />
        ) : (
          <>
            <Title level={4}>
              {confirmationDetails?.name ? (
                `Congratulations ${confirmationDetails?.name ?? ""} on Your New Health Insurance Plan!`
              ) : (
                <LoadingOutlined />
              )}
            </Title>
            <Paragraph type="secondary">
              Your purchase was successful, welcome onboard!
            </Paragraph>

            <Card style={{ backgroundColor: "#e6f7ff", marginBottom: 24 }}>
              <Space>
                <QuestionCircleOutlined />
                <Text strong>Confirmation Email</Text>
              </Space>
              <Paragraph>
                {confirmationDetails?.sponsor_email ? (
                  `We've sent a confirmation email to ${confirmationDetails?.sponsor_email ?? <LoadingOutlined />}. This email summarizes the information
              below and includes access to your customer portal. Keep this email as a reference! An email has also
              been sent to the email provided for the principal member to access the EdenCare App!`
                ) : (
                  <LoadingOutlined />
                )}
              </Paragraph>
            </Card>

            <Title level={5}>What You Need to Know About Your Policy</Title>
            <Card style={{ backgroundColor: "#f7f8fa", marginTop: 24 }}>
              <ConfigProvider
                theme={{
                  components: {
                    Descriptions: {
                      labelBg: "inherit",
                    },
                  },
                }}
              >
                <Descriptions column={1} bordered>
                  <Descriptions.Item label="Policy Number">
                    {confirmationDetails?.policy_number ?? <LoadingOutlined />}
                    <CopyOutlined
                      style={{ marginLeft: 8, cursor: "pointer" }}
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="Policy Effective Date">
                    {confirmationDetails?.policy_start_date ? (
                      `${moment(
                        confirmationDetails?.policy_start_date,
                        "YYYY-MM-DD",
                      ).format("MMMM DD,YYYY")}`
                    ) : (
                      <LoadingOutlined />
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Premium Information">{`${
                    localStorage.getItem("coverageCountry") === "Kenya"
                      ? "KES"
                      : "RWF"
                  } ${parseInt(
                    confirmationDetails?.premium_amount,
                  ).toLocaleString("US")} (Paid in full)`}</Descriptions.Item>
                  <Descriptions.Item label="Policy Plan">
                    {confirmationDetails?.plan_name ?? <LoadingOutlined />}
                  </Descriptions.Item>
                </Descriptions>
              </ConfigProvider>
              {/* {confirmationDetails?.person_covered ? ( */}
              <>
                <Title level={5} style={{ marginTop: 24 }}>
                  People Covered
                </Title>
                <List
                  dataSource={
                    confirmationDetails?.person_covered?.length
                      ? confirmationDetails.person_covered.map((person) =>
                          `${person.first_name || ""} ${person.last_name || ""}`.trim(),
                        )
                      : []
                  }
                  renderItem={(item) => (
                    <List.Item>
                      <CheckCircleOutlined
                        style={{ marginRight: 8, color: "#00a6bd" }}
                      />
                      {item}
                    </List.Item>
                  )}
                />
              </>

              <Title level={5} style={{ marginTop: 24 }}>
                Key Coverage Points
              </Title>
              <List
                dataSource={[
                  "Comprehensive coverage for major medical services, including hospitalization and surgery",
                  "outpatient care, optical and dental(If Applicable)",
                  "Prescription drugs",
                  "Coverage for preventive care, including annual check-ups and immunizations.",
                ]}
                renderItem={(item) => (
                  <List.Item>
                    <CheckCircleOutlined
                      style={{ marginRight: 8, color: "#00a6bd" }}
                    />
                    {item}
                  </List.Item>
                )}
              />

              <Title level={5} style={{ marginTop: 24 }}>
                Waiting Periods
              </Title>
              <Paragraph style={{ fontWeight: 400 }}>
                All membership is subjected to specified waiting periods except
                for hospitalization following an accident which is covered from
                the policy start date.
              </Paragraph>
              <List
                dataSource={[
                  "There is a 21 days waiting period for all outpatient services under all benefits.",
                  "There is a 30 days waiting period for all admissions for illness",
                  "There is a 10 months waiting period for all maternity services and pregnancy-related conditions",
                  "12 months waiting period for treatment of all chronic, psychiatric, declared pre-existing, congenital conditions and all related conditions.",
                ]}
                renderItem={(item) => (
                  <List.Item>
                    <Text>{item}</Text>
                  </List.Item>
                )}
              />

              <Title level={5} style={{ marginTop: 24 }}>
                Notable Exclusions
              </Title>
              <List
                dataSource={[
                  "Pre-existing conditions, Cosmetic or Elective Surgeries, Experimental treatments, Self-inflicted injuries or suicide, and non-prescription drugs among others. See full list in Policy Document",
                ]}
                renderItem={(item) => (
                  <List.Item>
                    <Text>{item}</Text>
                  </List.Item>
                )}
              />
              {/* {confirmationDetails && confirmationDetails?.is_medical_assessment_required &&  */}
              <Card style={{ backgroundColor: "#dff7fb", marginTop: 24 }}>
                <Title level={5} style={{ margin: 0 }}>
                  Medical Assessment Required
                </Title>
                <List
                  dataSource={[
                    "To ensure we provide you with the best coverage,we need to conduct a medical assessment.This is required because:",
                    "One of the people covered is over the age of 50, or",
                    "You have indicated certain health conditions in your answers.",
                    "This assessment allows us to tailor your policy to meet your specific health needs at no additional cost. Click below to book your free medical assessment now.",
                  ]}
                  renderItem={(item, index) => (
                    <List.Item>
                      <Text className={styles.listText}>
                        {(index === 1 || index === 2) && (
                          <img src={checkFilled} alt="" />
                        )}
                        {item}
                      </Text>
                    </List.Item>
                  )}
                />
                {confirmationDetails?.name ? (
                  <Button
                    className={styles.verifyEmail}
                    onClick={() => navigate("/bookassessment")}
                  >
                    Book Medical Assessment
                  </Button>
                ) : (
                  <LoadingOutlined />
                )}
              </Card>
              {/* } */}
            </Card>
            <Space style={{ marginTop: 24 }}>
              <Button onClick={() => window.open(viewPolicy, "_blank")}>
                View full policy details
              </Button>
              <Button icon={<DownloadOutlined />} disabled>
                Download Invoice
              </Button>
            </Space>

            <Divider />

            <Title level={5}>How to Use Your Policy</Title>
            <Card style={{ backgroundColor: "#f7f8fa", marginTop: 24 }}>
              <Title level={5} style={{ marginTop: 24 }}>
                Finding Care
              </Title>
              <Paragraph>
                Visit our online provider directory to find in-network doctors
                and facilities.
              </Paragraph>

              <Title level={5} style={{ marginTop: 24 }}>
                Identification
              </Title>
              <Paragraph>
                Present your digital insurance card and a government-issued
                photo ID.
              </Paragraph>

              <Title level={5} style={{ marginTop: 24 }}>
                Making a Claim
              </Title>
              <Paragraph>
                No action is required for visits to network providers—we handle
                the claims for you.
              </Paragraph>

              <Title level={5} style={{ marginTop: 24 }}>
                Emergency Assistance
              </Title>
              <Paragraph>{`Contact us immediately at ${coverageCountry && coverageCountry === "Kenya" ? "+254 794 830 000" : coverageCountry === "Rwanda" ? "+250 788 124 800" : `XXXXXXX`}`}</Paragraph>

              <Title level={5}>Support</Title>
              <Paragraph>
                With your new plan we've assigned you a dedicated care manager.
                Penelope Umutesi will be in touch soon to help you get started
                and answer any questions you may have.
              </Paragraph>

              <Title level={5} style={{ marginTop: 24 }}>
                Links to Resources
              </Title>
              <Button
                type="link"
                style={{ color: "#00a6bd" }}
                icon={<QuestionCircleOutlined />}
                onClick={() =>
                  window.open(
                    "https://www.edencaremedical.com/resources/faq",
                    "_blank",
                  )
                }
              >
                FAQs
              </Button>
            </Card>
            <Divider />

            <Title level={5}>Explore Your Digital Tools</Title>
            <Card style={{ backgroundColor: "#f7f8fa", marginTop: 24 }}>
              <Title level={5} style={{ marginTop: 24 }}>
                Customer digital portal
              </Title>
              <Paragraph>
                Manage active policies, update information, and view billing
                details in your personal dashboard
              </Paragraph>

              <Title level={5} style={{ marginTop: 24 }}>
                Member App
              </Title>
              <List
                dataSource={[
                  "Digital Medical Cards: Access your digital insurance card on the go.",
                  "Real-Time Claims Tracking: Know the status of your claims anytime, anywhere.",
                  "Health & Wellness Resources: Get tips and resources to stay healthy.",
                ]}
                renderItem={(item) => (
                  <List.Item>
                    <CheckCircleOutlined
                      style={{ marginRight: 8, color: "#00a6bd" }}
                    />
                    {item}
                  </List.Item>
                )}
              />
            </Card>
            <Space style={{ marginTop: 24 }}>
              <Button type="primary" disabled>
                Explore customer portal
              </Button>
              <Button icon={<DownloadOutlined />}>Download member app</Button>
            </Space>

            <Divider />

            <Card style={{ backgroundColor: "#f7f8fa", marginTop: 24 }}>
              <Space align="start">
                <UserOutlined style={{ fontSize: 24 }} />
                <div>
                  <Text strong>Support</Text>
                  <Paragraph>
                    {`If you have any questions, please call us at ${coverageCountry && coverageCountry === "Kenya" ? "+254 794 830 000" : coverageCountry === "Rwanda" ? "+250 788 124 800" : `XXXXXXX`} or email us
                    at support@edencaremedical.com`}
                  </Paragraph>
                </div>
              </Space>
            </Card>
          </>
        )}
      </Card>
    </div>
  );
};

export default React.memo(Index);
